// src/pages/admin/AllAdminOrders.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";
import OrderSkeleton from "../common/OrderSkeleton";

const AllAdminOrders = () => {
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL;
  const token = Cookies.get("gameport");

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);

  // ✅ debounce search so API isn’t called every keystroke
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  // reset page when search changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const {
    data,
    isPending,
    isError,
    error,
    isFetching,
  } = useQuery({
    queryKey: ["admin-orders", debouncedSearch, page],
    queryFn: async () => {
      const url = new URL(`${API_URL}/order/all-orders`);
      url.searchParams.set("search", debouncedSearch);
      url.searchParams.set("page", String(page));

      const res = await fetch(url.toString(), {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || "Failed to fetch orders");
      return json;
    },
    enabled: Boolean(API_URL),
    keepPreviousData: true,
  });

  const orders = data?.orders ?? [];

  // if your API returns these, we’ll use them. If not, it still works.
  const totalPages = data?.totalPages;
  const hasNext =
    typeof data?.hasNext === "boolean"
      ? data.hasNext
      : typeof totalPages === "number"
      ? page < totalPages
      : orders.length > 0; // fallback guess

  const hasPrev = page > 1;

  const goToOrder = (id) => navigate(`/admin/orders/${id}`);

  if (isPending) return <OrderSkeleton />;

  if (isError) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sm:p-6">
        <p className="bg-red 600 text-sm">{error?.message}</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sm:p-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
        <div className="min-w-0">
          <h2 className="text-base sm:text-lg font-semibold text-gray-800">
            All Orders
          </h2>
          <p className="text-xs sm:text-sm text-gray-500">
            Search and open any order to view details.
          </p>
        </div>

        <div className="w-full sm:w-80">
          <input
            className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-800
              focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-300"
            placeholder="Search order or customer…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Subtle fetching indicator (when changing page/search) */}
      {isFetching && (
        <p className="text-xs text-gray-500 mb-3">Updating…</p>
      )}

      {/* ✅ Mobile: Cards */}
      <div className="grid grid-cols-1 gap-3 sm:hidden">
        {orders.length === 0 ? (
          <div className="text-sm text-gray-600 py-6 text-center">
            No orders found.
          </div>
        ) : (
          orders.map((order) => (
            <button
              key={order._id}
              type="button"
              onClick={() => goToOrder(order._id)}
              className="text-left rounded-2xl border border-gray-100 bg-gray-50 p-4 active:scale-[0.99] transition"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold text-gray-900 truncate">
                    #{order?.orderCode || order?._id}
                  </p>
                  <p className="text-sm text-gray-600 truncate">
                    {order?.user?.fullName || "Unknown customer"}
                  </p>
                </div>

                <span className="shrink-0 text-xs font-semibold capitalize rounded-full bg-white border border-gray-200 px-2 py-1 text-blue-700">
                  {order?.orderStatus || "N/A"}
                </span>
              </div>

              <div className="mt-2 flex items-center justify-between text-sm">
                <p className="text-gray-600">Amount</p>
                <p className="font-semibold text-gray-900">
                  ₦{Number(order?.total || 0).toLocaleString()}
                </p>
              </div>
            </button>
          ))
        )}
      </div>

      {/* ✅ Desktop/Tablet: Table */}
      <div className="hidden sm:block overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="border-b text-gray-500">
            <tr>
              <th className="text-left py-3 pr-4 font-medium">Order ID</th>
              <th className="text-left py-3 pr-4 font-medium">Customer</th>
              <th className="text-left py-3 pr-4 font-medium">Status</th>
              <th className="text-left py-3 font-medium">Amount</th>
            </tr>
          </thead>

          <tbody>
            {orders.length === 0 ? (
              <tr>
                <td colSpan={4} className="py-8 text-center text-gray-600">
                  No orders found.
                </td>
              </tr>
            ) : (
              orders.map((order) => (
                <tr
                  key={order._id}
                  onClick={() => goToOrder(order._id)}
                  className="border-b hover:bg-gray-50 cursor-pointer"
                >
                  <td className="py-3 pr-4 font-medium text-gray-900">
                    #{order?.orderCode || order?._id}
                  </td>
                  <td className="py-3 pr-4 text-gray-700">
                    {order?.user?.fullName || "Unknown customer"}
                  </td>
                  <td className="py-3 pr-4 text-blue-600 capitalize font-medium">
                    {order?.orderStatus || "N/A"}
                  </td>
                  <td className="py-3 text-gray-900 font-semibold">
                    ₦{Number(order?.total || 0).toLocaleString()}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-5">
        <p className="text-xs sm:text-sm text-gray-500">
          Page <span className="font-semibold text-gray-800">{page}</span>
          {typeof totalPages === "number" ? ` of ${totalPages}` : ""}
        </p>

        <div className="flex gap-2">
          <button
            type="button"
            disabled={!hasPrev}
            onClick={() => setPage((p) => Math.max(p - 1, 1))}
            className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm hover:bg-gray-50
              disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Prev
          </button>

          <button
            type="button"
            disabled={!hasNext}
            onClick={() => setPage((p) => p + 1)}
            className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm hover:bg-gray-50
              disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default AllAdminOrders;
